// src/api.js
import axios from 'axios';
import { Device } from '@capacitor/device';

let deviceId = null;

const getDeviceId = async () => {
    // If we already have a device ID, return it
    if (deviceId) {
        console.debug('Using cached device ID:', deviceId);
        return deviceId;
    }

    try {
        // First try to get the device ID from Capacitor
        const info = await Device.getId();
        if (info && info.uuid) {
            deviceId = info.uuid;
            console.debug('Got device ID from Capacitor:', deviceId);
            // Store it in localStorage as backup
            localStorage.setItem('deviceId', deviceId);
            return deviceId;
        }
    } catch (e) {
        console.debug('Failed to get device ID from Capacitor:', e);
    }

    // If Capacitor failed, try to get from localStorage
    deviceId = localStorage.getItem('deviceId');
    if (deviceId) {
        console.debug('Got device ID from localStorage:', deviceId);
        return deviceId;
    }

    // If all else fails, generate a new UUID
    try {
        deviceId = crypto.randomUUID();
        console.debug('Generated new device ID:', deviceId);
        localStorage.setItem('deviceId', deviceId);
        return deviceId;
    } catch (e) {
        // Fallback for browsers that don't support crypto.randomUUID()
        deviceId = 'web_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
        console.debug('Generated fallback device ID:', deviceId);
        localStorage.setItem('deviceId', deviceId);
        return deviceId;
    }
};

// Initialize device ID immediately
(async () => {
    try {
        await getDeviceId();
        console.debug('Device ID initialized successfully:', deviceId);
    } catch (e) {
        console.error('Failed to initialize device ID:', e);
    }
})();

// Create axios instance with base configuration
const api = axios.create({
    baseURL: 'https://truthorjuicy.vercel.app',
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add request interceptor to set device ID header
api.interceptors.request.use(async (config) => {
    try {
        if (!deviceId) {
            console.debug('Device ID not set, getting it now...');
            await getDeviceId();
        }
        // Ensure the header is set with the current device ID
        config.headers['Device-ID'] = deviceId;
        console.debug('Request headers:', config.headers);
        return config;
    } catch (error) {
        console.error('Error in request interceptor:', error);
        // Even if we fail to get a device ID, we should still try to make the request
        // The server will handle the missing device ID case
        return config;
    }
});

// Add response interceptor for debugging
api.interceptors.response.use(
    response => response,
    error => {
        console.error('API request failed:', {
            url: error.config?.url,
            method: error.config?.method,
            headers: error.config?.headers,
            data: error.config?.data,
            status: error.response?.status,
            statusText: error.response?.statusText,
            responseData: error.response?.data
        });
        return Promise.reject(error);
    }
);

export default api;
